import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, LayoutTemplate, Users, LogOut, ChevronsUpDown, Check,
  Settings, Building2, ShieldCheck, Bell, Eye, ThumbsUp, ThumbsDown, Cpu, CreditCard, MessageCircle, Plus, Gem, Mail, SlidersHorizontal,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import Logo from "./Logo";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const baseNav = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/proposals", label: "Propositions", icon: FileText, testid: "nav-proposals" },
  { to: "/templates", label: "Modèles", icon: LayoutTemplate, testid: "nav-templates" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
];

function Chip({ b, px = 24 }) {
  const style = { width: px, height: px };
  return b.logo_file_id ? (
    <img src={fileUrl(b.logo_file_id)} alt={b.name} style={style}
      className="rounded-sm object-contain bg-white border border-border p-0.5 shrink-0" />
  ) : (
    <span style={{ ...style, backgroundColor: b.accent_hex || "#0047FF" }}
      className="rounded-sm flex items-center justify-center text-[10px] font-bold text-white shrink-0">
      {(b.logo_letter || b.name?.[0] || "?").toUpperCase()}
    </span>
  );
}

function CompanySwitcher({ companiesData, activeCompany, brand, onSwitch, onNew }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="company-switcher"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-sm border border-border hover:border-primary transition-all duration-200">
        <Chip b={brand} />
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-semibold truncate leading-tight">{brand.name}</p>
          <p className="text-[10px] text-muted-foreground truncate">{brand.industry}</p>
        </div>
        <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60 rounded-sm">
        <DropdownMenuLabel className="text-xs">Changer de société</DropdownMenuLabel>
        {companiesData.map((b) => (
          <DropdownMenuItem key={b.slug} onClick={() => onSwitch(b.slug)} data-testid={`switch-${b.slug}`} className="gap-2">
            <Chip b={b} px={20} />
            <span className="flex-1 truncate">{b.name}</span>
            {b.slug === activeCompany && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onNew} className="gap-2 text-primary font-medium">
          <Plus className="w-4 h-4" /> Nouvelle société
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NOTIF_ICON = {
  first_view: Eye,
  accepted: ThumbsUp,
  refused: ThumbsDown,
  question: MessageCircle,
  deposit_paid: CreditCard,
  email_open: Mail,
};

function NotificationBell({ activeCompany }) {
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [activeCompany]);

  const onOpen = (open) => {
    if (open && data.unread > 0) {
      api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 }))).catch(() => {});
    }
  };

  return (
    <DropdownMenu onOpenChange={onOpen}>
      <DropdownMenuTrigger data-testid="notifications-bell" className="relative p-2 rounded-sm hover:bg-muted">
        <Bell className="w-5 h-5 text-muted-foreground" />
        {data.unread > 0 && (
          <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {data.unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-sm max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="text-xs">Notifications</DropdownMenuLabel>
        {data.items.length === 0 && <p className="px-3 py-6 text-sm text-muted-foreground text-center">Aucune notification.</p>}
        {data.items.map((n) => {
          const Icon = NOTIF_ICON[n.type] || Eye;
          return (
            <div key={n.id} className={`flex gap-3 px-3 py-2.5 border-b border-border last:border-0 ${!n.read ? "bg-primary/5" : ""}`}>
              <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
              <div className="min-w-0">
                <p className="text-sm leading-snug">
                  <span className="font-medium">{n.title}</span>
                  {n.type === "email_open" ? " — email ouvert par le prospect" : <> — un prospect {n.message}</>}
                </p>
                <p className="text-[11px] text-muted-foreground">{new Date(n.ts).toLocaleString("fr-FR")}</p>
              </div>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout, companies, companiesData, activeCompany, setActiveCompany, fetchCompanies, brand, role, isSuperadmin } = useAuth();
  const navigate = useNavigate();

  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const createCompany = async () => {
    if (!newCompanyName.trim()) return;
    setCreatingCompany(true);
    try {
      const { data } = await api.post("/companies", { name: newCompanyName });
      toast.success(`Société "${data.name}" créée !`);
      setNewCompanyOpen(false);
      setNewCompanyName("");
      const updatedCompanies = await fetchCompanies();
      setActiveCompany(data.slug, updatedCompanies);
      navigate("/settings");
    } catch (e) {
      toast.error(e.response?.data?.message || "Erreur lors de la création");
    } finally { setCreatingCompany(false); }
  };

  const canManage = ["owner", "admin"].includes(role) || isSuperadmin;
  const nav = [...baseNav];
  if (role === "owner") nav.push({ to: "/billing", label: "Abonnement", icon: Gem, testid: "nav-billing" });
  if (canManage) nav.push({ to: "/settings", label: "Paramètres", icon: Settings, testid: "nav-settings" });
  if (isSuperadmin) {
    nav.push({ to: "/admin/companies", label: "Sociétés", icon: Building2, testid: "nav-admin-companies" });
    nav.push({ to: "/admin/users", label: "Utilisateurs", icon: ShieldCheck, testid: "nav-admin-users" });
    nav.push({ to: "/admin/settings", label: "Paramètres plateforme", icon: SlidersHorizontal, testid: "nav-admin-settings" });
    nav.push({ to: "/admin/emails", label: "Modèles d'email", icon: Mail, testid: "nav-admin-emails" });
    nav.push({ to: "/admin/billing", label: "Facturation", icon: CreditCard, testid: "nav-admin-billing" });
  }

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-surface">
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-background shrink-0 fixed h-screen">
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <Logo brand={brand} />
          <NotificationBell activeCompany={activeCompany} />
        </div>
        {companiesData && brand && (
          <div className="p-3 border-b border-border">
            <CompanySwitcher
              companiesData={companiesData}
              activeCompany={activeCompany}
              brand={brand}
              onSwitch={setActiveCompany}
              onNew={() => setNewCompanyOpen(true)}
            />
          </div>
        )}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}>
              <item.icon className="w-[18px] h-[18px]" strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-sm bg-foreground text-white flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">{isSuperadmin ? "Super-admin" : role}</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-muted-foreground hover:bg-muted hover:text-destructive transition-all duration-200">
            <LogOut className="w-[18px] h-[18px]" strokeWidth={2} />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile top bar — safe area en haut (notch / Dynamic Island iOS) */}
      <div className="lg:hidden fixed top-0 inset-x-0 bg-background border-b border-border z-40 flex flex-col">
        <div className="h-safe-top" />
        <div className="h-14 flex items-center justify-between px-4">
          <Logo brand={brand} />
          <div className="flex items-center gap-1">
            <NotificationBell activeCompany={activeCompany} />
            {brand && (
              <button onClick={() => setMoreOpen(true)} data-testid="company-switcher-mobile"
                className="flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border text-xs font-medium ml-1">
                <Chip b={brand} px={16} />
                <ChevronsUpDown className="w-3 h-3 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* mobile-pt = h-14 + safe-area-inset-top ; mobile-pb = h-16 + safe-area-inset-bottom */}
      <main key={activeCompany} className="flex-1 lg:ml-64 mobile-pt lg:pt-0 mobile-pb lg:pb-0 min-w-0">
        <Outlet />
      </main>

      {/* Dialog création de société */}
      {newCompanyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background border border-border rounded-sm p-6 w-full max-w-md shadow-xl mx-4">
            <h2 className="font-heading text-lg font-bold mb-1">Nouvelle société</h2>
            <p className="text-sm text-muted-foreground mb-4">Créez une société indépendante avec son propre abonnement, ses propositions et son équipe.</p>
            <input
              autoFocus
              type="text"
              placeholder="Nom de la société"
              value={newCompanyName}
              onChange={e => setNewCompanyName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createCompany()}
              className="w-full border border-border rounded-sm px-3 py-2 text-sm mb-4 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setNewCompanyOpen(false); setNewCompanyName(""); }}
                className="px-4 py-2 text-sm rounded-sm border border-border hover:bg-muted transition-colors">
                Annuler
              </button>
              <button onClick={createCompany} disabled={creatingCompany || !newCompanyName.trim()}
                className="px-4 py-2 text-sm rounded-sm bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors">
                {creatingCompany ? "Création…" : "Créer la société"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom nav — safe area en bas (home indicator iOS) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-background border-t border-border z-40 flex flex-col">
        <div className="h-16 flex">
          {baseNav.map((item) => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-1 text-[10px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <item.icon className="w-5 h-5" />
              {item.label.split(" ")[0]}
            </NavLink>
          ))}
          {(canManage || isSuperadmin) && (
            <button onClick={() => setMoreOpen(true)}
              className="flex-1 flex flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none"/>
              </svg>
              Plus
            </button>
          )}
        </div>
        <div className="h-safe-bottom" />
      </nav>

      {/* Drawer "Plus" mobile */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
          <div className="relative bg-background rounded-t-xl border-t border-border shadow-xl">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
            </div>

            {/* Infos utilisateur */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-border">
              <div className="w-10 h-10 rounded-sm bg-foreground text-white flex items-center justify-center text-sm font-semibold shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{isSuperadmin ? "Super-admin" : role}</p>
              </div>
            </div>

            <div className="p-3 space-y-0.5">
              {/* Settings */}
              {canManage && (
                <NavLink to="/settings" onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-foreground hover:bg-muted"}`}>
                  <Settings className="w-5 h-5" /> Paramètres
                </NavLink>
              )}

              {/* Admin — superadmin uniquement */}
              {isSuperadmin && (
                <>
                  <div className="px-4 pt-3 pb-1">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">Administration</p>
                  </div>
                  {[
                    { to: "/admin/companies", label: "Sociétés", icon: Building2 },
                    { to: "/admin/users", label: "Utilisateurs", icon: ShieldCheck },
                    { to: "/admin/settings", label: "Paramètres plateforme", icon: SlidersHorizontal },
                    { to: "/admin/emails", label: "Modèles d'email", icon: Mail },
                    { to: "/admin/billing", label: "Facturation & codes promo", icon: CreditCard },
                  ].map((item) => (
                    <NavLink key={item.to} to={item.to} onClick={() => setMoreOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-foreground hover:bg-muted"}`}>
                      <item.icon className="w-5 h-5" /> {item.label}
                    </NavLink>
                  ))}
                </>
              )}

              {/* Company switcher mobile */}
              {companiesData && companiesData.length > 0 && (
                <>
                  <div className="px-4 pt-3 pb-1">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">Société active</p>
                  </div>
                  {companiesData.map((b) => (
                    <button key={b.slug} onClick={() => { setActiveCompany(b.slug); setMoreOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium text-foreground hover:bg-muted transition-colors">
                      <Chip b={b} px={20} />
                      <span className="flex-1 text-left truncate">{b.name}</span>
                      {b.slug === activeCompany && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  ))}
                  <button onClick={() => { setMoreOpen(false); setNewCompanyOpen(true); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium text-primary hover:bg-muted transition-colors">
                    <Plus className="w-5 h-5" /> Nouvelle société
                  </button>
                </>
              )}

              {/* Déconnexion */}
              <div className="pt-2 border-t border-border mt-2">
                <button onClick={() => { setMoreOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors">
                  <LogOut className="w-5 h-5" /> Déconnexion
                </button>
              </div>
            </div>
            <div className="h-safe-bottom" />
          </div>
        </div>
      )}
    </div>
  );
}

